import React from 'react';
import moment from 'moment';
import { Popover } from 'ui-components/popover/popover';
import { useSelector } from 'react-redux';
import { driversSelector } from 'slices/drivers/driversSlice';
import { ReactComponent as EditIcon } from 'images/edit_solid.svg';
import { useDriverDetails } from '../hooks/useDriverDetails';
import TerminalDetails from '../terminalDetails';
import Loading from 'ui-components/loading/loading';
import { endorsementMapping } from 'app/data/drivers/models';
import { ReactComponent as TickIcon } from 'images/tick.svg';
import { ReactComponent as AlertIcon } from 'images/alert_circle.svg';
import { userSelector } from 'slices/user/userSlice';
import { UserUtils } from 'app/data/user/userUtils';

const DriverDetails = ({ handleEdit }: { handleEdit: () => void }) => {
  const driverState = useSelector(driversSelector);
  const userState = useSelector(userSelector);
  const isXGSAdmin = UserUtils.isXGSAdministrator(userState?.profile);
  const { driverDetails, isLoadingDriverDetails } = useDriverDetails(driverState?.selectedDriver?.id?.toString() || "");

  const { endorsements, restrictions } =
    driverDetails?.data?.commercialDriverLicense || {};

  const renderRestrictionCodes = (items: { code: string; isActive: boolean }[] | undefined) => {
    if (!items || items.length === 0) return <div>N/A</div>;

    const activeItems = items.filter((item) => item.isActive);

    if (activeItems.length === 0) return <div>N/A</div>;

    return (
      <>
        {activeItems.map((item) => (
          <span key={item.code} className="drivers-sidebar__renderCodes__restriction">
            <AlertIcon width={16} height={16} />
            <span className="drivers-sidebar__renderCodes__restriction__code">{item.code}</span>
          </span>
        ))}
      </>
    );
  };

  const renderEndorsementsCodes = (
    items: { code: string; isActive: boolean }[] | undefined
  ) => {
    if (!items || items.length === 0) return <div>N/A</div>;

    const activeItems = items.filter((item) => item.isActive);
    if (activeItems.length === 0) return <div>N/A</div>;

    return (
      <>
        {activeItems.map((item) => {
          const label = endorsementMapping[item.code as keyof typeof endorsementMapping];
          return (
            <span key={item.code} className="drivers-sidebar__renderCodes__endorsement">
              <TickIcon width={16} height={16} />
              {label ? <><span className="drivers-sidebar__renderCodes__endorsement__code">{item.code}</span> - {label}</>
                : <span className="drivers-sidebar__renderCodes__endorsement__code">{item.code}</span>}
            </span>
          );
        })}
      </>
    );
  };


  return <>
    <div className='drivers-sidebar__header'>
      <div className='drivers-sidebar__title'>Driver Information</div>
      <div className='drivers-sidebar__edit' onClick={handleEdit}>
        <div className='drivers-sidebar__edit__icon'>
          <EditIcon width={14} height={14} className='drivers-sidebar__edit__icon' />
        </div>
        <div className='drivers-sidebar__edit__text'>Edit</div>
      </div>
    </div>
    {isLoadingDriverDetails ? <Loading isLoading={isLoadingDriverDetails} /> :
      <>
        <div className='drivers-sidebar__content'>
          <div className='drivers-sidebar__content__item'>
            <div className='drivers-sidebar__content__item__label'>Driver ID{' '}
              <span className='drivers-sidebar__content__item__auto-generated'>
                (Auto-generated)</span></div>
            <div className='drivers-sidebar__content__item__value'>{driverDetails?.data?.driverId}</div>
          </div>
          <div className='drivers-sidebar__content__item'>
            <div className='drivers-sidebar__content__item__label'>Name</div>
            <div className='drivers-sidebar__content__item__value'>{driverDetails?.data?.firstName + " " + driverDetails?.data?.lastName}</div>
          </div>
          <div className='drivers-sidebar__content__item'>
            <div className='drivers-sidebar__content__item__label'>CDL # <Popover values={<div className='driver-details-tooltip'>CDL stands for Commercial Driver’s License number — a unique number issued to each driver</div>} /></div>
            <div className='drivers-sidebar__content__item__value'>{driverDetails?.data?.commercialDriverLicense?.licenseNumber ?? 'N/A'}</div>
          </div>
          <div className='drivers-sidebar__content__item'>
            <div className='drivers-sidebar__content__item__label'>CDL Class <Popover values={<div className='driver-details-tooltip'>Indicates the type of vehicle the driver is licensed to operate. Class A: large trucks, Class B: buses, Class C: smaller vehicles.</div>} /></div>
            <div className='drivers-sidebar__content__item__value'>{driverDetails?.data?.commercialDriverLicense?.licenseClass ?? 'N/A'}</div>
          </div>
          <div className='drivers-sidebar__content__item'>
            <div className='drivers-sidebar__content__item__label'>Address</div>
            <div className='drivers-sidebar__content__item__value'>
              {driverDetails?.data?.address ?
                driverDetails?.data?.address.street + ", " +
                driverDetails?.data?.address.city + ", " : 'N/A'}</div>
            <div className='drivers-sidebar__content__item__value'>
              {driverDetails?.data?.address ?
                driverDetails?.data?.address.state + ", " +
                driverDetails?.data?.address.zipcode : ''}</div>
          </div>
          <div className='drivers-sidebar__content__item'>
            <div className='drivers-sidebar__content__item__label'>Phone</div>
            <div className='drivers-sidebar__content__item__value'>{
              driverDetails?.data?.phone ?
                (driverDetails?.data?.phone?.formatPhone()?.length > 0 ? driverDetails?.data?.phone?.formatPhone() : driverDetails?.data?.phone) : 'N/A'}</div>
          </div>
          <div className='drivers-sidebar__content__item'>
            <div className='drivers-sidebar__content__item__label'>Terminal <Popover values={<div className='driver-details-tooltip'>The driver’s assigned work location or dispatch point.</div>} /></div>
            <div className='drivers-sidebar__content__item__value'>
              {<TerminalDetails terminalNumber={driverDetails?.data?.domicileTerminalNumber || ""} />}
            </div>
          </div>
          <div className='drivers-sidebar__content__item'>
            <div className='drivers-sidebar__content__item__label'>Status</div>
            <div className='drivers-sidebar__content__item__value'>{driverDetails?.data?.status === "active" ?
              <div className="drivers-list__status drivers-list__status__active">{driverDetails?.data?.status}</div> :
              <div className="drivers-list__status drivers-list__status__inactive">{driverDetails?.data?.status}</div>}</div>
          </div>

          {isXGSAdmin && (
            <div className='drivers-sidebar__content__item'>
              <div className='drivers-sidebar__content__item__label'>Compensation</div>
              <div className='drivers-sidebar__content__item__value'>
                Hourly Rate: {driverDetails?.data?.hourlyRate ?? 'N/A'}
              </div>
              <div
                className='drivers-sidebar__content__item__value'
                style={{ marginTop: '4px' }}
              >
                Per-Mile Rate: {driverDetails?.data?.perMileRate ?? 'N/A'}
              </div>
            </div>
          )}

          <div className='drivers-sidebar__content__item'>
            <div className='drivers-sidebar__content__item__label'>License Expiration</div>
            <div className='drivers-sidebar__content__item__value'>{
              driverDetails?.data?.commercialDriverLicense?.expirationDate ?
                moment(driverDetails?.data?.commercialDriverLicense?.expirationDate).format("MM-DD-YYYY") : "N/A"}</div>
          </div>
        </div>
        <div className='drivers-sidebar__content'>
          <div className='drivers-sidebar__content__subtitle'>Endorsements</div>
          <div className='drivers-sidebar__content__item__value'>
            {renderEndorsementsCodes(endorsements)}
          </div>
        </div>
        <div className='drivers-sidebar__content'>
          <div className='drivers-sidebar__content__subtitle'>Restrictions</div>
          <div className='drivers-sidebar__content__item__value'>
            {renderRestrictionCodes(restrictions)}
          </div>
        </div>
      </>
    }
  </>;
};

export default DriverDetails;
